/**
 * AppointmentReminderService
 * Sends appointment reminders (email + SMS) to clients and logs them to Firestore.
 * Singleton pattern consistent with other services in this project.
 */

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  doc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import firebaseAuthService from './firebase/FirebaseAuthService';

// ── Types ────────────────────────────────────────────────────────────────────

export interface AppointmentReminderParams {
  appointmentId: string;
  patientName: string;
  patientEmail: string;
  patientPhone?: string;
  providerName: string;
  appointmentDate: Date;
  startTime: string; // e.g. "10:00"
  modality: 'video' | 'in-person';
  sessionType: string;
}

export interface ReminderRecord {
  id: string;
  appointmentId: string;
  patientName: string;
  providerName: string;
  sentAt: Timestamp;
  type: 'manual' | '24h' | '1h';
  channels: string[];
  status: 'sent' | 'failed';
}

export interface SendReminderResult {
  emailSent: boolean;
  smsSent: boolean;
}

// ── Service ──────────────────────────────────────────────────────────────────

class AppointmentReminderService {
  private static instance: AppointmentReminderService;

  static getInstance(): AppointmentReminderService {
    if (!AppointmentReminderService.instance) {
      AppointmentReminderService.instance = new AppointmentReminderService();
    }
    return AppointmentReminderService.instance;
  }

  // ── Send Reminder ────────────────────────────────────────────────────────

  async sendAppointmentReminder(
    params: AppointmentReminderParams,
    type: 'manual' | '24h' | '1h' = 'manual'
  ): Promise<SendReminderResult> {
    const {
      appointmentId,
      patientName,
      patientEmail,
      patientPhone,
      providerName,
      appointmentDate,
      startTime,
      modality,
      sessionType
    } = params;

    let emailSent = false;
    let smsSent = false;
    const channels: string[] = [];

    // ── Send email ─────────────────────────────────────────────────────────
    try {
      emailSent = await this.sendReminderEmail({
        patientName,
        patientEmail,
        providerName,
        appointmentDate,
        startTime,
        modality,
        sessionType
      });
      if (emailSent) channels.push('email');
    } catch (err) {
      console.error('[AppointmentReminder] Email send failed:', err);
    }

    // ── Send SMS ───────────────────────────────────────────────────────────
    if (patientPhone) {
      try {
        smsSent = await this.sendReminderSms({
          patientPhone,
          providerName,
          appointmentDate,
          startTime,
          modality
        });
        if (smsSent) channels.push('sms');
      } catch (err) {
        console.error('[AppointmentReminder] SMS send failed:', err);
      }
    }

    // ── Log to Firestore ───────────────────────────────────────────────────
    const status = (emailSent || smsSent) ? 'sent' : 'failed';

    try {
      await addDoc(collection(db, 'appointmentReminders'), {
        appointmentId,
        patientName,
        providerName,
        sentAt: Timestamp.now(),
        type,
        channels,
        status
      });
    } catch (err) {
      console.error('[AppointmentReminder] Failed to log reminder:', err);
    }

    // ── Mark appointment as reminded ───────────────────────────────────────
    if (emailSent || smsSent) {
      try {
        const appointmentRef = doc(db, 'appointments', appointmentId);
        await updateDoc(appointmentRef, { reminderSent: true });
      } catch (err) {
        console.error('[AppointmentReminder] Failed to update appointment:', err);
      }
    }

    return { emailSent, smsSent };
  }

  // ── Get Sent Reminders ───────────────────────────────────────────────────

  async getSentReminders(
    providerId: string,
    startDate?: Date,
    endDate?: Date
  ): Promise<ReminderRecord[]> {
    try {
      const remindersRef = collection(db, 'appointmentReminders');

      const constraints: any[] = [
        orderBy('sentAt', 'desc')
      ];

      if (startDate) {
        constraints.push(where('sentAt', '>=', Timestamp.fromDate(startDate)));
      }
      if (endDate) {
        constraints.push(where('sentAt', '<=', Timestamp.fromDate(endDate)));
      }

      const q = query(remindersRef, ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      })) as ReminderRecord[];
    } catch (err) {
      console.error('[AppointmentReminder] Failed to fetch reminders:', err);
      return [];
    }
  }

  // ── Private: Email ───────────────────────────────────────────────────────

  private async sendReminderEmail(params: {
    patientName: string;
    patientEmail: string;
    providerName: string;
    appointmentDate: Date;
    startTime: string;
    modality: 'video' | 'in-person';
    sessionType: string;
  }): Promise<boolean> {
    const { patientName, patientEmail, providerName, appointmentDate, startTime, modality, sessionType } = params;

    if (!patientEmail) return false;

    const token = await firebaseAuthService.getIdToken();
    if (!token) {
      console.warn('[AppointmentReminder] No auth token — cannot send email');
      return false;
    }

    const firstName = patientName.split(' ')[0] || 'there';
    const siteUrl = window.location.origin;
    const formattedDate = appointmentDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const modalityLabel = modality === 'video' ? 'Video Session' : 'In-Person Visit';

    const subject = 'Appointment Reminder — MyClinicCore';

    const htmlBody = `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 24px;">
        <div style="background-color: #0f172a; border-radius: 12px; padding: 32px; color: #f8fafc;">
          <h2 style="margin: 0 0 20px 0; color: #6CA6C1;">MyClinicCore</h2>

          <p style="margin: 0 0 16px 0; font-size: 16px;">Dear ${firstName},</p>

          <p style="margin: 0 0 16px 0; font-size: 15px; color: #94a3b8;">
            This is a friendly reminder about your upcoming appointment:
          </p>

          <div style="background-color: #1e293b; border-radius: 8px; padding: 20px; margin: 0 0 20px 0;">
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 6px 0; font-size: 14px; color: #64748b; width: 110px;">Date</td>
                <td style="padding: 6px 0; font-size: 15px; color: #f8fafc; font-weight: 600;">${formattedDate}</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; font-size: 14px; color: #64748b;">Time</td>
                <td style="padding: 6px 0; font-size: 15px; color: #f8fafc; font-weight: 600;">${startTime}</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; font-size: 14px; color: #64748b;">Provider</td>
                <td style="padding: 6px 0; font-size: 15px; color: #f8fafc; font-weight: 600;">${providerName}</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; font-size: 14px; color: #64748b;">Type</td>
                <td style="padding: 6px 0; font-size: 15px; color: #f8fafc; font-weight: 600;">${modalityLabel}</td>
              </tr>
              <tr>
                <td style="padding: 6px 0; font-size: 14px; color: #64748b;">Session</td>
                <td style="padding: 6px 0; font-size: 15px; color: #f8fafc; font-weight: 600;">${sessionType}</td>
              </tr>
            </table>
          </div>

          <div style="margin: 20px 0; text-align: center;">
            <a href="${siteUrl}"
               style="display: inline-block; padding: 14px 28px; background-color: #6CA6C1; color: #0f172a;
                      text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 15px;">
              View Your Appointment
            </a>
          </div>

          <hr style="border: none; border-top: 1px solid #334155; margin: 24px 0;" />

          <p style="margin: 0; font-size: 13px; color: #64748b;">
            We look forward to seeing you at your appointment.
          </p>
          <p style="margin: 8px 0 0 0; font-size: 13px; color: #64748b;">
            Best regards,<br/>
            <strong style="color: #94a3b8;">${providerName}</strong><br/>
            MyClinicCore
          </p>
        </div>

        <p style="margin: 16px 0 0 0; font-size: 11px; color: #888; text-align: center;">
          This is an automated reminder. Please do not reply to this email.
        </p>
      </div>
    `;

    const textBody = `Dear ${firstName},

This is a friendly reminder about your upcoming appointment:

Date: ${formattedDate}
Time: ${startTime}
Provider: ${providerName}
Type: ${modalityLabel}
Session: ${sessionType}

Visit ${siteUrl} to view your appointment details.

We look forward to seeing you at your appointment.

Best regards,
${providerName}
MyClinicCore

This is an automated reminder. Please do not reply to this email.`;

    try {
      const response = await fetch('/api/send-notification-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          to: patientEmail,
          subject,
          htmlBody,
          textBody
        }),
      });

      const data = await response.json();

      if (data.sent) {
        return true;
      } else {
        console.warn('[AppointmentReminder] Email not sent:', data.reason || data.error);
        return false;
      }
    } catch (err) {
      console.error('[AppointmentReminder] Email request failed:', err);
      return false;
    }
  }

  // ── Private: SMS ─────────────────────────────────────────────────────────

  private async sendReminderSms(params: {
    patientPhone: string;
    providerName: string;
    appointmentDate: Date;
    startTime: string;
    modality: 'video' | 'in-person';
  }): Promise<boolean> {
    const { patientPhone, providerName, appointmentDate, startTime, modality } = params;

    const token = await firebaseAuthService.getIdToken();
    if (!token) {
      console.warn('[AppointmentReminder] No auth token — cannot send SMS');
      return false;
    }

    const formattedDate = appointmentDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
    const modalityLabel = modality === 'video' ? 'Video' : 'In-person';
    const siteUrl = window.location.origin;

    const message = `MyClinicCore Reminder: You have an appointment with ${providerName} on ${formattedDate} at ${startTime}. ${modalityLabel}. Log in at ${siteUrl} to prepare.`;

    try {
      const response = await fetch('/api/send-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          to: patientPhone,
          message
        }),
      });

      const data = await response.json();

      if (data.sent) {
        return true;
      } else {
        console.warn('[AppointmentReminder] SMS not sent:', data.reason || data.error);
        return false;
      }
    } catch (err) {
      console.error('[AppointmentReminder] SMS request failed:', err);
      return false;
    }
  }
}

export const appointmentReminderService = AppointmentReminderService.getInstance();
export default AppointmentReminderService;
